import React from "react";
import {
  Box,
  Container,
  Alert,
  AlertIcon,
  Text,
  Flex,
  useColorMode,
} from "@chakra-ui/react";
import { TFunction } from "i18next";

interface FallbackLanguageBarProps {
  fallbackLanguage: string | null;
  getLanguageDisplayName: (code: string, displayLanguage: string) => string;
  t: TFunction;
  i18n: any;
  isFixed?: boolean; // To handle fixed positioning for ReadMode
}

const FallbackLanguageBar: React.FC<FallbackLanguageBarProps> = ({
  fallbackLanguage,
  getLanguageDisplayName,
  t,
  i18n,
  isFixed = false,
}) => {
  const { colorMode } = useColorMode();

  if (!fallbackLanguage) return null;

  // Common styles for both versions
  const alertStyles = {
    status: "warning" as const,
    variant: "subtle" as const,
    py: 2,
    fontSize: "sm",
    justifyContent: "flex-start",
    bg:
      colorMode === "light"
        ? "rgba(240, 240, 240, 0.9)"
        : "rgba(30, 35, 45, 0.9)",
    color: colorMode === "light" ? "gray.800" : "white",
    boxShadow:
      colorMode === "light"
        ? "0px 2px 10px rgba(0,0,0,0.1)"
        : "0px 2px 10px rgba(0,0,0,0.3)",
    alignItems: "center",
    width: "100%",
  };

  // Wrapper that changes based on whether it's fixed or not
  const Wrapper = ({ children }: { children: React.ReactNode }) => {
    if (isFixed) {
      return (
        <Box position="fixed" top="0" left="0" right="0" zIndex="toast">
          <Container maxW="container.md" px={{ base: 3, md: 4 }}>
            {children}
          </Container>
        </Box>
      );
    }
    return (
      <Box
        sx={{
          maxWidth: { base: "100%", md: "100%", lg: "95%", xl: "90%" },
          mx: "auto", // Center the alert horizontally
          width: "100%",
        }}
      >
        {children}
      </Box>
    );
  };

  return (
    <Wrapper>
      <Alert
        {...alertStyles}
        // Keep consistent style: always have rounded borders, with margin in non-fixed mode
        borderRadius={isFixed ? "0 0 md md" : "md"}
        mb={isFixed ? 0 : 4}
      >
        <Flex align="center" width="100%" gap={2}>
          <AlertIcon
            color={colorMode === "light" ? "orange.500" : "orange.300"}
            alignSelf="center"
            boxSize="20px"
            marginTop="0"
            marginRight="0"
            display="flex"
          />
          <Text
            fontWeight="medium"
            lineHeight="1.4"
            display="flex"
            alignItems="center"
          >
            {i18n.language === "ru"
              ? "Эта книга недоступна на выбранном языке. Отображается оригинальная версия книги."
              : t(
                  "readBook.fallbackLanguageNotice",
                  "This book is not available in {{requestedLanguage}}. Displaying the original {{fallbackLanguage}} version.",
                  {
                    requestedLanguage: getLanguageDisplayName(
                      i18n.language,
                      i18n.language
                    ),
                    fallbackLanguage: getLanguageDisplayName(
                      fallbackLanguage || "",
                      i18n.language
                    ),
                  }
                )}
          </Text>
        </Flex>
      </Alert>
    </Wrapper>
  );
};

export default FallbackLanguageBar;
